import { CalendarDays } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { dateInputToLocalDate, localDateToDateInput } from '@/lib/calendar-date'
import { formatDateOnly } from '@/lib/format'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

/**
 * Tek gün seçimi: yerel `<input type="date">` yerine (DESIGN §5) uiarc `Calendar` açan tetikleyici.
 * Değer `YYYY-MM-DD` (yerel tarih girişiyle aynı biçim), boş string = seçim yok.
 */
export function DateInput({
  value,
  onChange,
  className,
  placeholder = messages.calendar.pickDate,
  compact = false,
  autoFocus,
  onFocus,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  className?: string
  placeholder?: string
  /** Dar hücre (tablo filtre satırı): simge yok, yıl iki hane (`15.10.26`); tam tarih aria-label'da. */
  compact?: boolean
  autoFocus?: boolean
  onFocus?: () => void
  'aria-label'?: string
}) {
  const [open, setOpen] = useState(false)
  const date = dateInputToLocalDate(value || undefined)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            // Filtre satırı çipten dönünce odağı geri verir.
            autoFocus={autoFocus}
            onFocus={onFocus}
            aria-label={ariaLabel && date ? `${ariaLabel}: ${formatDateOnly(value)}` : ariaLabel}
            className={cn(
              'border-border-strong bg-surface text-foreground rounded-control hover:border-foreground focus-visible:border-foreground ease-standard flex h-(--control-height-md) w-full min-w-0 cursor-pointer items-center gap-1.5 border px-3 text-left text-sm tabular-nums transition-[border-color] duration-(--duration-fast) outline-none motion-reduce:transition-none',
              className,
            )}
          />
        }
      >
        {!compact && (
          <CalendarDays className="text-text-muted size-3.5 shrink-0" aria-hidden="true" />
        )}
        <span className={cn('min-w-0 truncate', !date && 'text-text-muted')}>
          {date
            ? compact
              ? formatDateOnly(value).replace(/\.(\d{2})(\d{2})$/, '.$2')
              : formatDateOnly(value)
            : placeholder}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-none p-3">
        <Calendar
          value={date}
          showToday
          onChange={(next) => {
            onChange(localDateToDateInput(next))
            setOpen(false)
          }}
        />
        {date && (
          <Button
            variant="ghost"
            size="sm"
            className="self-end"
            onClick={() => {
              onChange('')
              setOpen(false)
            }}
          >
            {messages.common.clear}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
